import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { AsesoriaPlan } from './useSupabaseAdmin';

const mockPlans: AsesoriaPlan[] = [
  {
    id: 1,
    name: 'Esencial',
    price: '1.200€',
    period: 'por temporada',
    description: 'Revisión completa de armario y definición de estilo base para quien quiere empezar con firmeza.',
    features: [
      'Análisis de perfil psicológico y comunicación personal',
      'Revisión del armario actual (hasta 60 prendas)',
      'Definición de paleta de color personal',
      'Creación de 12 looks estratégicos',
      'Lista de compras prioritaria',
      '1 sesión de seguimiento (2h)',
    ],
    cta: 'Reservar consulta',
    highlighted: false,
    sort_order: 0,
  },
  {
    id: 2,
    name: 'Premium',
    price: '3.800€',
    period: 'por temporada',
    description: 'Transformación integral con sistema digital y asesoría continua. El equilibrio perfecto.',
    features: [
      'Todo lo incluido en Esencial',
      'Acceso al sistema digital de armario inteligente',
      'Registro digital de todas las prendas',
      'Combinaciones inteligentes automatizadas',
      'Calendario de looks planificado',
      'Asesoría en corte de cabello y barba',
      'Protocolo de cuidado de piel personalizado',
      '4 sesiones de seguimiento (8h total)',
      'Personal shopper acompañado (4h)',
    ],
    cta: 'Solicitar evaluación',
    highlighted: true,
    sort_order: 1,
  },
  {
    id: 3,
    name: 'Integral',
    price: '7.500€',
    period: 'anual',
    description: 'Acompañamiento completo durante un año. Tu imagen, evolucionada bajo supervisión directa.',
    features: [
      'Todo lo incluido en Premium',
      'Diseño y confección de 3 prendas a medida',
      'Organización mensual del armario',
      'Actualización continua del sistema digital',
      'Asesoría en maquillaje y definición estética',
      'Planificación de looks para eventos especiales',
      'Sesiones trimestrales de revisión (12h total)',
      'Personal shopper ilimitado dentro del plan',
      'Línea directa con Anthony Sánchez',
    ],
    cta: 'Entrevista exclusiva',
    highlighted: false,
    sort_order: 2,
  },
];

export function useSupabaseAsesoriaPlans() {
  const [data, setData] = useState<AsesoriaPlan[]>(mockPlans);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (hasFetched) return;

    const fetchData = async () => {
      const timeoutId = setTimeout(() => {
        setLoading(false);
        setError('Tiempo de espera agotado. Usando datos locales.');
      }, 3000);

      try {
        const { data: rows, error: err } = await supabase
          .from('asesoria_plans')
          .select('*')
          .order('sort_order', { ascending: true });

        clearTimeout(timeoutId);

        if (err) {
          setError(err.message);
          setData(mockPlans);
        } else if (rows && rows.length > 0) {
          setData(rows as AsesoriaPlan[]);
        } else {
          setData(mockPlans);
        }
      } catch {
        clearTimeout(timeoutId);
        setError('Error de conexión. Usando datos locales.');
        setData(mockPlans);
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    };

    fetchData();
  }, [hasFetched]);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from('asesoria_plans')
        .select('*')
        .order('sort_order', { ascending: true });

      if (err) {
        setError(err.message);
      } else if (rows) {
        setData(rows as AsesoriaPlan[]);
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refresh };
}